import { Link } from "react-router-dom";
import { useInView } from "../../../hooks/useInView";

export default function VenueSection() {
  const { ref, inView } = useInView();

  return (
    <section className="w-full bg-zinc-900 relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 bg-diagonal-stripe-dark pointer-events-none" />
      <div className="absolute top-0 left-0 w-96 h-96 bg-orange-500/8 rounded-full blur-3xl pointer-events-none animate-glow-pulse" />
      <div className="absolute bottom-0 right-0 w-64 h-64 border-r border-b border-orange-500/12 pointer-events-none" />

      <div
        ref={ref as React.RefObject<HTMLElement>}
        className="relative max-w-7xl mx-auto px-6 lg:px-10 py-14 md:py-20"
      >
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-10 lg:gap-16">
          {/* Left: Venue Info */}
          <div className={`flex-1 anim-hidden ${inView ? "animate-fade-in-left" : ""}`}>
            <div className="inline-block border border-zinc-700 text-zinc-400 font-condensed font-semibold text-xs uppercase tracking-widest px-3 py-1 mb-5">
              Our Venue
            </div>
            <h2 className="font-condensed font-black text-white text-4xl md:text-5xl uppercase tracking-tight leading-none mb-4">
              Metroflex<br />
              <span className="text-orange-500">Sports Complex</span>
            </h2>
            <div className={`h-0.5 bg-orange-500/40 w-20 mb-5 anim-hidden ${inView ? "line-grow delay-200" : ""}`} />
            <div className="flex flex-col gap-3 mt-2">
              {[
                { icon: "ri-map-pin-2-fill", main: "11300 Knott Ave", sub: "Stanton, CA 90680" },
                { icon: "ri-time-fill", main: "Doors Open at 6:00 PM", sub: "Shows Start at 7:00 PM" },
                { icon: "ri-parking-box-fill", main: "Free Parking On-Site", sub: null },
              ].map((item, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-3 anim-hidden ${inView ? "animate-fade-in-left" : ""}`}
                  style={{ animationDelay: `${200 + i * 100}ms` }}
                >
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <i className={`${item.icon} text-orange-500`} />
                  </div>
                  <div>
                    <p className="text-white font-condensed font-bold text-base uppercase tracking-wide">
                      {item.main}
                    </p>
                    {item.sub && (
                      <p className="text-zinc-400 font-condensed text-sm uppercase tracking-wide">
                        {item.sub}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className={`mt-8 anim-hidden ${inView ? "animate-fade-in-up delay-500" : ""}`}>
              <Link
                to="/events"
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-condensed font-bold text-sm uppercase tracking-widest px-7 py-3.5 transition-all duration-200 cursor-pointer whitespace-nowrap group"
              >
                Get Directions
                <i className="ri-navigation-line transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          {/* Right: Google Maps Embed */}
          <div
            className={`flex-shrink-0 w-full lg:w-[480px] h-[280px] md:h-[320px] overflow-hidden border border-zinc-700 anim-hidden ${inView ? "animate-fade-in-right delay-200" : ""}`}
          >
            <iframe
              title="Metroflex Sports Complex"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3314.0285!2d-117.9993!3d33.8019!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80dd29c2a5a5a5a5%3A0x0!2sMetroflex+Gym%2C+11300+Knott+Ave%2C+Stanton%2C+CA+90680!5e0!3m2!1sen!2sus!4v1680000000000!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0, filter: "grayscale(100%) invert(90%) contrast(90%)" }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
